'use client';

import { Disclosure, Transition } from '@headlessui/react';
import { MakeLargerIcon, MakeShortenIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';

import { FC } from 'react';
import { cn } from 'utils/cn';

interface IExpander {
  limit?: number;
  children?: string;
  className?: string;
  classNameChildren?: string;
  classNamePanel?: string;
  classNameDisclosure?: string;
  classNameContainer?: string;
  classNameTitle?: string;
  defaultOpen?: boolean;
}

const Expander: FC<IExpander> = ({
  children,
  limit = 165,
  defaultOpen,
  className,
  classNameChildren,
  classNamePanel,
  classNameDisclosure,
  classNameContainer
}) => {
  const t = useTranslations();
  const cutString = (str = '', limit: number): string => {
    if (str.length <= limit) return str;
    const trimmed = str.slice(0, limit);
    const lastSpaceIndex = trimmed.lastIndexOf(' ');

    return lastSpaceIndex > 0 ? trimmed.slice(0, lastSpaceIndex) + '...' : trimmed + '...';
  };

  const text = cutString(children, limit);

  return (
    <div className={cn('relative my-4 w-full', className)}>
      <div className={cn('mb-3 w-full max-w-md', classNameContainer)}>
        <Disclosure defaultOpen={defaultOpen}>
          {({ open }) => (
            <div
              className={cn(
                'rounded-5xl transition-colors duration-200 ease-in-out',
                'bg-tertiary-800',
                open && 'border-1 border-tertiary-600 outline-none',
                classNameDisclosure
              )}
            >
              {(children as string).length > limit && (
                <Disclosure.Button
                  className={cn(
                    'absolute right-0 z-50 h-[calc(100%_-_12px)]',
                    'transition-colors duration-200 ease-in-out',
                    'group flex w-full items-center justify-between',
                    'text-left text-sm font-medium',
                    'focus-visible:ring-offset-primary-blue-500/75',
                    'focus:outline-none focus-visible:ring',
                    'py-2 pl-4 pr-2',
                    'text-tertiary-100',
                    classNameChildren,
                    'justify-end',
                    open
                      ? 'rounded-t-3xl bg-primary-blue-900 hover:bg-primary-blue-600'
                      : 'rounded-4xl bg-primary-blue-600 hover:bg-primary-blue-900'
                  )}
                >
                  <div
                    data-tooltip-id="tolq-tooltip"
                    data-tooltip-content={open ? t('tooltips.shorter') : t('tooltips.expand')}
                    className="absolute right-2 top-2 z-50"
                  >
                    {!open && <MakeLargerIcon className="!text-primary-blue-100" />}
                    {open && <MakeShortenIcon className="!text-primary-blue-100" />}
                  </div>
                </Disclosure.Button>
              )}
              {!open && (
                <p className="copy !m-0 !mb-3 rounded-3xl border-1 border-tertiary-600 bg-tertiary-800 p-3 py-2">
                  {text}
                </p>
              )}
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Disclosure.Panel className={cn(classNamePanel)}>
                  <p className="copy !m-0 !mb-3 p-3 py-2">{children}</p>
                </Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>
      </div>
    </div>
  );
};

export default Expander;
